import { AtlasUserOverride } from 'api/atlasClient'
import { Expression, FieldPredicate } from 'api/hulkApi'

import { getMarketAnalysisSegment } from './marketAnalysisSegmentsApi'

type FilterWindow = 't30' | 't90' | 't180' | 't365' | 't730'

const REVENUE_WINDOW_FIELDS: Record<FilterWindow, string> = {
    t30: 'revenue_total_last_4_weeks',
    t90: 'revenue_total_last_12_weeks',
    t180: 'revenue_total_last_26_weeks',
    t365: 'revenue_total_last_52_weeks',
    t730: 'revenue_total_last_104_weeks',
}

const PRICE_WINDOW_FIELDS: Record<FilterWindow, string> = {
    t30: 'buybox_price_median_last_4_weeks',
    t90: 'buybox_price_median_last_12_weeks',
    t180: 'buybox_price_median_last_26_weeks',
    t365: 'buybox_price_median_last_52_weeks',
    t730: 'buybox_price_median_last_104_weeks',
}

const UNITS_SOLD_WINDOW_FIELDS: Record<FilterWindow, string> = {
    t30: 'sales_total_last_4_weeks',
    t90: 'sales_total_last_12_weeks',
    t180: 'sales_total_last_26_weeks',
    t365: 'sales_total_last_52_weeks',
    t730: 'sales_total_last_104_weeks',
}

const STAR_RATING_FIELD = 'latest_week_parent_rating_average'
const STAR_RATING_MIN = 0
const STAR_RATING_MAX = 5

const REVIEW_COUNT_FIELD = 'latest_week_variant_review_count'

const TITLE_FIELD = 'title_searchable'

interface RangeFilterValue {
    window: FilterWindow
    min?: number
    max?: number
}

interface StarRatingFilterValue {
    min: number
    max: number
}

interface ReviewCountFilterValue {
    min?: number
    max?: number
}

interface TitleFilterRule {
    id: string
    operator: 'contains' | 'not_contains'
    matchMode: 'any' | 'all'
    keywords: string[]
}

interface TitleFilterGroup {
    id: string
    rules: TitleFilterRule[]
}

interface TitleFilterValue {
    groups: TitleFilterGroup[]
}

interface SellerOption {
    id: string
    name?: string
}

interface SegmentQueryDefinition {
    marketplace?: string
    categories?: string[]
    brands?: string[]
    excludedBrands?: string[]
    sellers?: SellerOption[]
    excludedSellers?: SellerOption[]
    titleFilter?: TitleFilterValue
    revenueFilter?: RangeFilterValue
    unitsSoldFilter?: RangeFilterValue
    priceFilter?: RangeFilterValue
    starRatingFilter?: StarRatingFilterValue
    reviewCountFilter?: ReviewCountFilterValue
    includedAsins?: string[]
    excludedAsins?: string[]
}

function buildMinMaxExpression(
    field: string,
    min: number | undefined,
    max: number | undefined
): Expression | undefined {
    const predicates: Expression[] = []
    if (min != null) {
        predicates.push({ field, operator: '_gte', value: min })
    }
    if (max != null) {
        predicates.push({ field, operator: '_lte', value: max })
    }
    if (predicates.length === 0) {
        return undefined
    }
    if (predicates.length === 1) {
        return predicates[0]
    }
    return { _and: predicates }
}

function buildRevenueExpression(
    value: RangeFilterValue | undefined
): Expression | undefined {
    if (!value) {
        return undefined
    }
    const field = REVENUE_WINDOW_FIELDS[value.window]
    if (!field) {
        return undefined
    }
    return buildMinMaxExpression(field, value.min, value.max)
}

function buildUnitsSoldExpression(
    value: RangeFilterValue | undefined
): Expression | undefined {
    if (!value) {
        return undefined
    }
    const field = UNITS_SOLD_WINDOW_FIELDS[value.window]
    if (!field) {
        return undefined
    }
    return buildMinMaxExpression(field, value.min, value.max)
}

function buildPriceExpression(
    value: RangeFilterValue | undefined
): Expression | undefined {
    if (!value) {
        return undefined
    }
    const field = PRICE_WINDOW_FIELDS[value.window]
    if (!field) {
        return undefined
    }
    return buildMinMaxExpression(field, value.min, value.max)
}

function buildStarRatingExpression(
    value: StarRatingFilterValue | undefined
): Expression | undefined {
    if (!value) {
        return undefined
    }
    if (value.min === STAR_RATING_MIN && value.max === STAR_RATING_MAX) {
        return undefined
    }
    const predicates: Expression[] = []
    if (value.min > STAR_RATING_MIN) {
        predicates.push({
            field: STAR_RATING_FIELD,
            operator: '_gte',
            value: value.min,
        })
    }
    if (value.max < STAR_RATING_MAX) {
        predicates.push({
            field: STAR_RATING_FIELD,
            operator: '_lte',
            value: value.max,
        })
    }
    if (predicates.length === 0) {
        return undefined
    }
    if (predicates.length === 1) {
        return predicates[0]
    }
    return { _and: predicates }
}

function buildReviewCountExpression(
    value: ReviewCountFilterValue | undefined
): Expression | undefined {
    if (!value) {
        return undefined
    }
    return buildMinMaxExpression(REVIEW_COUNT_FIELD, value.min, value.max)
}

function buildTitleRuleExpression(
    rule: TitleFilterRule
): Expression | undefined {
    if (rule.keywords.length === 0) {
        return undefined
    }
    const operator =
        rule.operator === 'contains' ? '_contains' : '_not_contains'
    const predicates: FieldPredicate[] = rule.keywords.map((kw) => ({
        field: TITLE_FIELD,
        operator,
        value: kw,
    }))
    if (predicates.length === 1) {
        return predicates[0]
    }
    const useOr = (rule.matchMode === 'any') === (rule.operator === 'contains')
    return useOr ? { _or: predicates } : { _and: predicates }
}

function buildTitleExpression(
    value: TitleFilterValue | undefined
): Expression | undefined {
    if (!value || value.groups.length === 0) {
        return undefined
    }
    const groupExpressions: Expression[] = []
    for (const group of value.groups) {
        const ruleExpressions: Expression[] = []
        for (const rule of group.rules) {
            const expr = buildTitleRuleExpression(rule)
            if (expr) {
                ruleExpressions.push(expr)
            }
        }
        if (ruleExpressions.length === 1) {
            groupExpressions.push(ruleExpressions[0])
        } else if (ruleExpressions.length > 1) {
            groupExpressions.push({ _or: ruleExpressions })
        }
    }
    if (groupExpressions.length === 0) {
        return undefined
    }
    if (groupExpressions.length === 1) {
        return groupExpressions[0]
    }
    return { _and: groupExpressions }
}

function categoryIdsToNumbers(ids: string[]): number[] {
    return ids.map((cid) => Number(String(cid).replace('_', '')))
}

function buildMarketplaceExpression(
    marketplace: string | null | undefined
): Expression | undefined {
    if (!marketplace) {
        return undefined
    }
    return {
        field: 'marketplace',
        operator: '_eq',
        value: marketplace,
    }
}

function compileSegmentQueryDefinitionExcludingMarketplace(
    qd: SegmentQueryDefinition | undefined
): Expression | undefined {
    if (!qd) {
        return undefined
    }
    const predicates: Expression[] = []

    const categoryIds = Array.isArray(qd.categories)
        ? qd.categories.filter((id) => id)
        : []
    if (categoryIds.length > 0) {
        predicates.push({
            field: 'breadcrumb_leaf_node_id_stable',
            operator: '_in',
            value: categoryIdsToNumbers(categoryIds),
        })
    }

    if (Array.isArray(qd.brands) && qd.brands.length > 0) {
        predicates.push({
            field: 'brand_stable',
            operator: '_in',
            value: qd.brands,
        })
    }
    if (Array.isArray(qd.excludedBrands) && qd.excludedBrands.length > 0) {
        predicates.push({
            field: 'brand_stable',
            operator: '_not_in',
            value: qd.excludedBrands,
        })
    }

    if (Array.isArray(qd.sellers) && qd.sellers.length > 0) {
        predicates.push({
            field: 'seller_id',
            operator: '_in',
            value: qd.sellers.map((s) => s.id),
        })
    }
    if (Array.isArray(qd.excludedSellers) && qd.excludedSellers.length > 0) {
        predicates.push({
            field: 'seller_id',
            operator: '_not_in',
            value: qd.excludedSellers.map((s) => s.id),
        })
    }

    const titleExpr = buildTitleExpression(qd.titleFilter)
    if (titleExpr) {
        predicates.push(titleExpr)
    }

    const revenueExpr = buildRevenueExpression(qd.revenueFilter)
    if (revenueExpr) {
        predicates.push(revenueExpr)
    }

    const unitsSoldExpr = buildUnitsSoldExpression(qd.unitsSoldFilter)
    if (unitsSoldExpr) {
        predicates.push(unitsSoldExpr)
    }

    const priceExpr = buildPriceExpression(qd.priceFilter)
    if (priceExpr) {
        predicates.push(priceExpr)
    }

    const starRatingExpr = buildStarRatingExpression(qd.starRatingFilter)
    if (starRatingExpr) {
        predicates.push(starRatingExpr)
    }

    const reviewCountExpr = buildReviewCountExpression(qd.reviewCountFilter)
    if (reviewCountExpr) {
        predicates.push(reviewCountExpr)
    }

    if (Array.isArray(qd.includedAsins) && qd.includedAsins.length > 0) {
        predicates.push({
            field: 'asin',
            operator: '_in',
            value: qd.includedAsins,
        })
    }

    if (Array.isArray(qd.excludedAsins) && qd.excludedAsins.length > 0) {
        predicates.push({
            field: 'asin',
            operator: '_not_in',
            value: qd.excludedAsins,
        })
    }

    if (predicates.length === 0) {
        return undefined
    }
    if (predicates.length === 1) {
        return predicates[0]
    }
    return { _and: predicates }
}

export function intersectExpressions(
    expressions: (Expression | undefined)[]
): Expression | undefined {
    const filtered = expressions.filter(
        (expr): expr is Expression => expr !== undefined
    )
    if (filtered.length === 0) {
        return undefined
    }
    if (filtered.length === 1) {
        return filtered[0]
    }
    return { _and: filtered }
}

export function compileSegmentQueryDefinition(
    qd: SegmentQueryDefinition | undefined
): Expression | undefined {
    if (!qd) {
        return undefined
    }
    const marketplaceExpr = buildMarketplaceExpression(qd.marketplace)
    const restExpr = compileSegmentQueryDefinitionExcludingMarketplace(qd)
    return intersectExpressions([marketplaceExpr, restExpr])
}

export function unionExpressions(
    expressions: Expression[]
): Expression | undefined {
    if (expressions.length === 0) {
        return undefined
    }
    if (expressions.length === 1) {
        return expressions[0]
    }
    return { _or: expressions }
}

export interface MarketAnalysisSegmentBaseFilters {
    expression: Expression | undefined
    // Lowercase marketplace shared by every selected segment, if any. The
    // picker locks selection to a single marketplace, but legacy state may
    // still produce a mixed set — in that case this is null.
    marketplace: string | null
}

export async function buildMarketAnalysisSegmentBaseFilters(
    segmentIds: string[],
    override?: AtlasUserOverride
): Promise<MarketAnalysisSegmentBaseFilters> {
    if (segmentIds.length === 0) {
        return { expression: undefined, marketplace: null }
    }
    // The segment definitions are fetched from Atlas too, so they need the same
    // owner-scoped token as the data query when impersonating during print.
    const requestConfig = override ? { userOverride: override } : undefined
    const segments = await Promise.all(
        segmentIds.map((id) => getMarketAnalysisSegment(id, requestConfig))
    )

    const restExprs: Expression[] = []
    const perSegmentExprs: Expression[] = []
    let sharedMarketplace: string | null | undefined = undefined
    let allShareMarketplace = true
    for (const s of segments) {
        const qd = s.query_definition as SegmentQueryDefinition
        const rest = compileSegmentQueryDefinitionExcludingMarketplace(qd)
        if (rest) {
            restExprs.push(rest)
        }
        const combined = intersectExpressions([
            buildMarketplaceExpression(s.marketplace),
            rest,
        ])
        if (combined) {
            perSegmentExprs.push(combined)
        }
        if (!s.marketplace) {
            allShareMarketplace = false
        } else if (sharedMarketplace === undefined) {
            sharedMarketplace = s.marketplace
        } else if (sharedMarketplace !== s.marketplace) {
            allShareMarketplace = false
        }
    }

    const resolvedMarketplace =
        allShareMarketplace && sharedMarketplace ? sharedMarketplace : null

    // Hoist marketplace to the outer AND when every segment agrees on it —
    // the picker enforces this, but fall back to per-segment embedding if
    // legacy state ever sneaks in a mismatched set.
    const expression = resolvedMarketplace
        ? intersectExpressions([
              buildMarketplaceExpression(resolvedMarketplace),
              unionExpressions(restExprs),
          ])
        : unionExpressions(perSegmentExprs)

    return { expression, marketplace: resolvedMarketplace }
}
